import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  ArrowLeft, Gift, Copy, Share2, Loader2, CheckCircle2, Clock, Ban,
  TrendingUp, Mail, MessageCircle, Twitter,
} from "lucide-react";

const Referrals = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/referrals/me")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Errore caricamento referral"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center gap-3 text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span className="font-mono text-xs tracking-widest uppercase">Loading referrals...</span>
      </div>
    );
  }
  if (!data) return null;

  const shareUrl = `${window.location.origin}/register?ref=${data.code}`;
  const capPct = Math.min(100, Math.round((data.ytd_credited_eur / data.annual_cap_eur) * 100));
  const aboveMin = data.balance_eur >= data.payout_min_eur;
  const remainingToPayout = Math.max(0, data.payout_min_eur - data.balance_eur);

  const onCopy = (text, label = "Copiato!") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };
  const shareText = `Sto usando Reelio per creare reel e caroselli con AI in 60 secondi 🔥 Se ti registri con il mio codice ${data.code} ricevi 30 giorni di Pro gratis: ${shareUrl}`;
  const shareWhatsapp = () =>
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener,noreferrer");
  const shareTwitter = () =>
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, "_blank", "noopener,noreferrer");
  const shareEmail = () =>
    (window.location.href = `mailto:?subject=${encodeURIComponent("Ti consiglio Reelio")}&body=${encodeURIComponent(shareText)}`);

  const statusBadge = (s) => {
    if (s === "credited")
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/10 text-green-400 text-[10px] font-mono uppercase tracking-widest border border-green-500/30 rounded-sm">
          <CheckCircle2 className="w-3 h-3" /> Pagato
        </span>
      );
    if (s === "capped")
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-500/10 text-zinc-400 text-[10px] font-mono uppercase tracking-widest border border-zinc-500/30 rounded-sm">
          <Ban className="w-3 h-3" /> Cap raggiunto
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 text-amber-400 text-[10px] font-mono uppercase tracking-widest border border-amber-500/30 rounded-sm">
        <Clock className="w-3 h-3" /> In attesa
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to="/dashboard" data-testid="referrals-back" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white mb-8 font-mono text-xs uppercase tracking-widest">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </Link>

        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-3">
          · Programma Partner
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-3">Invita & guadagna.</h1>
        <p className="text-zinc-400 font-body mb-10 max-w-2xl">
          Condividi il tuo codice. Chi si registra riceve <strong className="text-white">{data.reward_days} giorni di Pro gratis</strong>.
          Quando il tuo invitato fa il primo pagamento, ricevi il <strong className="text-white">{Math.round(data.cashback_ratio * 100)}%</strong> in cashback. Una sola volta, niente complicazioni.
        </p>

        {/* Top stats row */}
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5">
            <div className="flex items-center gap-2 text-zinc-500 text-[10px] font-mono uppercase tracking-widest mb-2">
              <Gift className="w-3 h-3" /> Saldo cashback
            </div>
            <div className="text-3xl font-display font-black tracking-tighter" data-testid="referrals-balance">€ {data.balance_eur.toFixed(2)}</div>
            <div className="text-[11px] font-body text-zinc-500 mt-2">
              {aboveMin ? (
                <span className="text-green-400">Pronto per il payout</span>
              ) : (
                <>Ancora <strong className="text-white">€{remainingToPayout.toFixed(2)}</strong> per richiedere il pagamento</>
              )}
            </div>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5">
            <div className="flex items-center gap-2 text-zinc-500 text-[10px] font-mono uppercase tracking-widest mb-2">
              <TrendingUp className="w-3 h-3" /> Anno corrente
            </div>
            <div className="text-3xl font-display font-black tracking-tighter" data-testid="referrals-ytd">€ {data.ytd_credited_eur.toFixed(2)}</div>
            <div className="mt-3">
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#FF4400] to-[#FFB800]"
                  style={{ width: `${capPct}%` }}
                />
              </div>
              <div className="text-[10px] font-mono text-zinc-500 mt-1.5">
                {capPct}% del cap annuale (€{data.annual_cap_eur.toLocaleString("it-IT")})
              </div>
            </div>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5">
            <div className="flex items-center gap-2 text-zinc-500 text-[10px] font-mono uppercase tracking-widest mb-2">
              <Mail className="w-3 h-3" /> Invitati totali
            </div>
            <div className="text-3xl font-display font-black tracking-tighter" data-testid="referrals-count">{data.referrals.length}</div>
            <div className="text-[11px] font-body text-zinc-500 mt-2">
              {data.referrals.filter((r) => r.commission_status === "credited").length} hanno fatto il primo pagamento
            </div>
          </div>
        </div>

        {/* Share box */}
        <div className="bg-gradient-to-br from-[#FF4400]/10 via-violet-500/5 to-transparent border border-[#FF4400]/30 rounded-sm p-6 mb-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4400] mb-3">· Il tuo codice</div>
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
            <div className="flex-1 bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-3 font-mono text-xl tracking-widest" data-testid="referrals-code">
              {data.code}
            </div>
            <button
              onClick={() => onCopy(data.code, "Codice copiato!")}
              data-testid="referrals-copy-code"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-display font-bold rounded-sm text-sm transition-colors"
            >
              <Copy className="w-4 h-4" /> Copia codice
            </button>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">· Link condivisibile</div>
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
            <div className="flex-1 bg-zinc-900 border border-zinc-800 rounded-sm px-4 py-3 font-body text-sm text-zinc-300 truncate" data-testid="referrals-share-url">
              {shareUrl}
            </div>
            <button
              onClick={() => onCopy(shareUrl, "Link copiato!")}
              data-testid="referrals-copy-url"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-display font-bold rounded-sm text-sm transition-colors"
            >
              <Copy className="w-4 h-4" /> Copia link
            </button>
          </div>
          {/* Social share buttons */}
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">· Condividi al volo</div>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={shareWhatsapp}
              data-testid="referrals-share-whatsapp"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-500 text-white font-display font-bold rounded-sm text-sm transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </button>
            <button
              onClick={shareTwitter}
              data-testid="referrals-share-twitter"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-sky-600 hover:bg-sky-500 text-white font-display font-bold rounded-sm text-sm transition-colors"
            >
              <Twitter className="w-4 h-4" /> Twitter
            </button>
            <button
              onClick={shareEmail}
              data-testid="referrals-share-email"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-zinc-700 hover:bg-zinc-600 text-white font-display font-bold rounded-sm text-sm transition-colors"
            >
              <Mail className="w-4 h-4" /> Email
            </button>
          </div>
        </div>

        {/* Mechanics explainer (collapsible? for now just inline) */}
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {[
            { n: "01", t: "Condividi", d: "Il tuo codice (o il link). Su WhatsApp, Instagram, email — dove vuoi." },
            { n: "02", t: "Un amico si registra", d: `Inserisce il tuo codice in fase di registrazione. Riceve subito ${data.reward_days} giorni di Pro gratis.` },
            { n: "03", t: "Tu guadagni", d: `Quando il tuo invitato fa il primo pagamento, ricevi il ${Math.round(data.cashback_ratio * 100)}% in cashback.` },
          ].map((step) => (
            <div key={step.n} className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-5">
              <div className="font-mono text-[28px] font-black text-[#FF4400] mb-2">{step.n}</div>
              <div className="font-display font-bold text-lg mb-1">{step.t}</div>
              <div className="text-[12px] font-body text-zinc-400">{step.d}</div>
            </div>
          ))}
        </div>

        {/* Referrals list */}
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">· I tuoi invitati</div>
        {data.referrals.length === 0 ? (
          <div className="bg-zinc-900/40 border border-dashed border-zinc-800 rounded-sm p-10 text-center">
            <Share2 className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 font-body">Nessun invitato ancora. Condividi il tuo codice qui sopra per iniziare!</p>
          </div>
        ) : (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Registrato</th>
                  <th className="px-4 py-3">Cashback</th>
                  <th className="px-4 py-3">Stato</th>
                </tr>
              </thead>
              <tbody className="font-body" data-testid="referrals-list">
                {data.referrals.map((r, i) => (
                  <tr key={i} className="border-b border-zinc-800/60 last:border-0">
                    <td className="px-4 py-3 text-zinc-300">{r.referee_email}</td>
                    <td className="px-4 py-3 text-zinc-500 text-[12px]">
                      {new Date(r.registered_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" })}
                    </td>
                    <td className="px-4 py-3 font-mono">
                      {r.commission_eur ? `€ ${r.commission_eur.toFixed(2)}` : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-4 py-3">{statusBadge(r.commission_status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Payout note */}
        <div className="mt-10 bg-zinc-900/40 border border-zinc-800 rounded-sm p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">· Come funziona il payout</div>
          <p className="text-[13px] font-body text-zinc-400 leading-relaxed">
            Quando il tuo saldo supera <strong className="text-white">€{data.payout_min_eur.toFixed(0)}</strong> potrai richiedere il pagamento via Stripe Connect (bonifico SEPA su IBAN o carta).
            Il processo richiede una verifica di identità rapida (KYC) e i fondi arrivano in 2-5 giorni lavorativi.
            <br /><br />
            <strong className="text-amber-400">Nota fiscale:</strong> il cashback è considerato reddito da prestazione occasionale. Sotto €5.000/anno non richiede P.IVA ma va dichiarato.
            Per protezione abbiamo impostato un cap automatico a <strong className="text-white">€{data.annual_cap_eur.toLocaleString("it-IT")}/anno</strong>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Referrals;
